'use client';

import { useSearchParams } from 'next/navigation';
import { Suspense, useCallback, useEffect, useState } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';

import { getCookieConsentConfig } from '@eliadmin/core/src/components/cookie-consent/cookie-consent-config';
import '@eliadmin/core/src/components/cookie-consent/cookie-consent.css';
import { CookieBar } from '@eliadmin/core/src/components/dlz/cookies/cookie-bar';
import {
  COOKIE_CATEGORIES,
  type CookieCategory,
} from '@eliadmin/core/src/components/dlz/cookies/cookie-categories';
import { CookieSettingsDialog } from '@eliadmin/core/src/components/dlz/cookies/cookie-settings-dialog';
import {
  OPEN_COOKIE_PREFERENCES_EVENT,
  OPEN_COOKIE_PREFERENCES_PARAM,
} from '@eliadmin/core/src/components/dlz/cookies/cookie-settings-request';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

declare global {
  interface Window {
    _eliadminConsentListenersBound?: boolean;
  }
}

/** `pending` until the engine has read the consent cookie; `off` for crawlers (no UI). */
type EngineState = 'pending' | 'ready' | 'off';

/**
 * vanilla-cookieconsent stays the consent engine (cookie, categories, revision, `data-category`
 * script blocking, `cc:*` events → gtag consent mode); its own modals are never shown
 * (`autoShow: false`, `#cc-main` hidden in `cookie-consent.css`) and the DLZ bar and settings
 * dialog drive it through `acceptCategory`.
 */
export function CookieConsentClient({ policyHref }: { policyHref?: string }) {
  const locale = useLocaleStore(state => state.currentLocale);
  const [engine, setEngine] = useState<EngineState>('pending');
  const [hasConsent, setHasConsent] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    listenForConsent();
    let isMounted = true;
    const config = { ...getCookieConsentConfig(locale), autoShow: false };

    void CookieConsent.run(config)
      .then(() => {
        if (!isMounted) return;
        setHasConsent(CookieConsent.validConsent());
        setEngine(config.hideFromBots !== false && isCrawler() ? 'off' : 'ready');
      })
      .catch(() => null);

    return () => {
      isMounted = false;
    };
  }, [locale]);

  const openSettings = useCallback(() => setSettingsOpen(true), []);

  useEffect(() => {
    const onOpenRequest = (event: Event) => {
      if (event instanceof CustomEvent && event.detail && typeof event.detail === 'object') {
        (event.detail as { handled?: boolean }).handled = true;
      }
      openSettings();
    };
    window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, onOpenRequest);
    return () => window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, onOpenRequest);
  }, [openSettings]);

  const decide = (categories: CookieCategory[] | 'all') => {
    CookieConsent.acceptCategory(categories);
    setHasConsent(true);
    setSettingsOpen(false);
  };

  const ready = engine === 'ready';
  const accepted = ready
    ? COOKIE_CATEGORIES.map(category => category.engine).filter(category =>
        CookieConsent.acceptedCategory(category),
      )
    : [];

  return (
    <>
      <Suspense fallback={null}>
        <OpenFromUrl onOpen={openSettings} />
      </Suspense>
      {/* Announces the bar politely when it appears; the bar itself never takes the focus. */}
      <div aria-live="polite">
        {ready && !hasConsent ? (
          <CookieBar
            policyHref={policyHref}
            concealed={settingsOpen}
            onAcceptAll={() => decide('all')}
            onRejectAll={() => decide([])}
            onOpenSettings={openSettings}
          />
        ) : null}
      </div>
      <CookieSettingsDialog
        // Requests before the engine is ready (an early `?occ=true`) open it once it is.
        open={ready && settingsOpen}
        onOpenChange={setSettingsOpen}
        accepted={accepted}
        onAcceptAll={() => decide('all')}
        onRejectAll={() => decide([])}
        onSave={decide}
      />
    </>
  );
}

/**
 * Opens the settings for `?occ=true` – on the first load and after client-side navigations – and
 * removes the parameter again. The state must not carry the router's own history keys: Next.js
 * then copies them and syncs `useSearchParams`, so the next `?occ=true` is a change again.
 */
function OpenFromUrl({ onOpen }: { onOpen: () => void }) {
  const requested = useSearchParams().get(OPEN_COOKIE_PREFERENCES_PARAM) === 'true';

  useEffect(() => {
    if (!requested) return;
    onOpen();
    const url = new URL(window.location.href);
    url.searchParams.delete(OPEN_COOKIE_PREFERENCES_PARAM);
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, [requested, onOpen]);

  return null;
}

/**
 * The engine's own `hideFromBots` test (it stops before reading the consent and does not expose
 * the result); crawlers get no bar, as with the core UI.
 */
function isCrawler() {
  return navigator.webdriver || /bot|crawl|spider|slurp|teoma/i.test(navigator.userAgent);
}

const listenForConsent = () => {
  if (window._eliadminConsentListenersBound) return;
  window._eliadminConsentListenersBound = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function () {
      // gtag.js expects the `arguments` object itself in the data layer.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };

  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
  });

  const updateGtagConsent = () => {
    window.gtag('consent', 'update', {
      ad_storage: CookieConsent.acceptedCategory('ads') ? 'granted' : 'denied',
      ad_user_data: CookieConsent.acceptedCategory('ads') ? 'granted' : 'denied',
      ad_personalization: CookieConsent.acceptedCategory('ads') ? 'granted' : 'denied',
      analytics_storage: CookieConsent.acceptedCategory('analytics') ? 'granted' : 'denied',
      functionality_storage: CookieConsent.acceptedCategory('functional') ? 'granted' : 'denied',
      personalization_storage: CookieConsent.acceptedCategory('functional') ? 'granted' : 'denied',
      security_storage: 'granted',
    });
    window.dataLayer?.push({
      event: 'cookie_consent_update',
    });
  };

  window.addEventListener('cc:onConsent', updateGtagConsent);
  window.addEventListener('cc:onChange', updateGtagConsent);
};
